import React, { useState } from 'react';
import { Box, Button, Stepper, Step, StepLabel, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import { useForm, FormProvider, SubmitHandler, FieldErrors } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// Import Step Components (Create these files)
import BasicInfoStep from '@features/hotels/components/steps/BasicInfoStep';
import OwnerDetailsStep from '@features/hotels/components/steps/OwnerDetailsStep';
import RenovationHistoryStep from '@features/hotels/components/steps/RenovationHistoryStep';
import ReviewSubmitStep from '@features/hotels/components/steps/ReviewSubmitStep';
import { HotelFormData,Hotel } from '@types'; // Adjust the import path as necessary

// Import the API service
import { api } from '@services';

// --- Constants ---
const steps = ['Basic Info', 'Renovation History', 'Owner Details', 'Review & Submit'];

// --- Component ---
const HotelCreationWizard: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Setup react-hook-form
  const methods = useForm<HotelFormData>({
    mode: 'onChange', // Validate on change for better UX
    defaultValues: {
      // Initialize default values
      name: '',
      description: '',
      property_type: '',
      chain_affiliation: '',
      website: '',
      owner_name: '',
      owner_type: '',
      owner_entity: '',
      owner_address: '',
      owner_city: '',
      owner_state: '',
      owner_zip: '',
      owner_country: 'USA',
      owner_contact_name: '',
      owner_contact_email: '',
      owner_contact_phone: '',
      owner_since: null,
      ownership_structure: '',
      ownership_notes: '',
      ownership_percentage: 100,
      acquisition_date: null,
      ownership_type_details: '',
      tax_id: '',
      legal_entity_type: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'USA',
      num_rooms: 0,
      star_rating: 0,
      year_built: 0,
      lot_size: 0,
      amenities: [],
      notes: '',
      renovation_history: [],
    },
  });

  const {
    handleSubmit,
    trigger,
    formState: { errors, isValid },
    getValues,
  } = methods;

  // Function to trigger validation for the current step's fields
  const triggerValidationForStep = async (stepIndex: number): Promise<boolean> => {
    let fieldsToValidate: (keyof HotelFormData)[] = [];
    switch (stepIndex) {
      case 0: // Basic Info Step
        fieldsToValidate = ['name', 'city', 'state'];
        break;
      case 1: // Renovation History Step
        // No required fields for renovation history
        break;
      case 2: // Owner Details Step
        // Owner contact email should be valid if provided
        fieldsToValidate = [];
        break;
      case 3: // Review Submit Step
        // Final validation of all required fields
        fieldsToValidate = ['name', 'city', 'state'];
        break;
      default:
        return true; // No validation needed
    }

    if (fieldsToValidate.length === 0) {
      return true; // No validation required for this step
    }

    // Trigger validation for the fields relevant to the current step
    const result = await trigger(fieldsToValidate as string[]);
    console.log(`Step ${stepIndex} validation result:`, result, 'Errors:', errors);
    return result;
  };

  // Handle moving to the next step
  const handleNext = async () => {
    // Validate current step before proceeding
    const isValidStep = await triggerValidationForStep(activeStep);

    if (isValidStep) {
      if (activeStep === steps.length - 1) {
        // If it's the last step, trigger the final form submission
        handleSubmit(onSubmit)(); // Call the submit handler
      } else {
        setActiveStep((prev) => prev + 1);
      }
    } else {
      // Optional: Show a general message if validation fails,
      // or rely on individual field errors displayed by the step component.
      console.log('Step validation failed, cannot proceed.');
    }
  };

  // Handle moving back
  const handleBack = () => setActiveStep((prev) => prev - 1);

  // Handle final form submission
  const onSubmit: SubmitHandler<HotelFormData> = async (data) => {
    setLoading(true);
    setSubmitError(null);
    // Add debug logging to see what's happening
    console.log('Amenities type before formatting:', typeof data.amenities);
    console.log('Is amenities array?', Array.isArray(data.amenities));
    console.log('Amenities raw value:', data.amenities);

    // Clean the amenities array to ensure it only contains valid strings
    const cleanedAmenities = Array.isArray(data.amenities) ? data.amenities.filter((item) => item !== null && item !== undefined && item.trim() !== '') : [];

    // Process renovation history - only include entries with all required fields
    const validRenovations = data.renovation_history
      .map((renovation) => ({
        year: Number(renovation.year) || 0,
        description: (renovation.description || '').trim(),
        cost: Number(renovation.cost) || 0,
      }))
      .filter((r) => r.year > 0 && r.description.length > 0 && r.cost >= 0); // Only include complete entries

    // Format data for API (similar to your previous logic, but data comes from react-hook-form)
    const formattedData = {
      // Map fields from 'data' object, perform conversions (Number, parseFloat, date format)
      name: (data.name || '').trim() || 'Unnamed Hotel',
      description: (data.description || '').trim(),
      property_type: data.property_type || '',
      chain_affiliation: data.chain_affiliation || '',
      website: (data.website || '').trim(),
      // Owner Info
      owner_name: data.owner_name || '',
      owner_type: data.owner_type || '',
      owner_entity: data.owner_entity || '',
      owner_address: data.owner_address || '',
      owner_city: data.owner_city || '',
      owner_state: data.owner_state || '',
      owner_zip: data.owner_zip || '',
      owner_country: data.owner_country || 'USA',
      owner_contact_name: data.owner_contact_name || '',
      owner_contact_email: data.owner_contact_email || '',
      owner_contact_phone: data.owner_contact_phone || '',
      owner_since: data.owner_since ? dayjs(data.owner_since).format('YYYY-MM-DD') : '',
      ownership_structure: data.ownership_structure || '',
      ownership_notes: data.ownership_notes || '',
      ownership_percentage: parseFloat(data.ownership_percentage) || 100,
      acquisition_date: data.acquisition_date ? dayjs(data.acquisition_date).format('YYYY-MM-DD') : '',
      ownership_type_details: data.ownership_type_details || '',
      tax_id: data.tax_id || '',
      legal_entity_type: data.legal_entity_type || '',
      // Location - ensure required fields are not null
      address: data.address || '',
      city: (data.city || '').trim() || 'Unknown',
      state: (data.state || '').trim() || 'Unknown',
      zip_code: (data.zip_code || '').trim(),
      country: data.country || 'USA',
      // Specs - handle numeric fields properly
      num_rooms: Math.max(0, Number(data.num_rooms) || 0),
      star_rating: Math.max(0, Math.min(5, Number(data.star_rating) || 0)),
      year_built: Number(data.year_built) || 0,
      lot_size: Number(data.lot_size) || 0,
      // Arrays/Text
      amenities: cleanedAmenities, // Keep as pure array, server will handle JSON conversion
      notes: data.notes || '',
      renovation_history: validRenovations, // Only send valid renovation entries
    };

    console.log('📤 Sending API Request:', JSON.stringify(formattedData, null, 2));

    try {
      // Use the actual API service to save the hotel
      const response = await api.saveHotelDetails(formattedData);

      console.log('✅ API Success Response:', response);
      navigate(`/hotels/${response.data.id}`); // Navigate to the new hotel page
    } catch (err: any) {
      console.error('❌ API Error Response:', err);
      setSubmitError(err.message || 'Error creating hotel');
    } finally {
      setLoading(false);
    }
  };

  // Function to render the content for the current step
  const renderStepContent = (step: number) => {
    // Pass react-hook-form methods down to step components
    // Note: You might only need to pass specific methods like register, control, errors
    switch (step) {
      case 0:
        return <BasicInfoStep />;
      case 1:
        return <RenovationHistoryStep />;
      case 2:
        return <OwnerDetailsStep />;
      case 3:
        return <ReviewSubmitStep />; // Pass getValues to Review step if needed
      default:
        return null;
    }
  };

  return (
    // FormProvider makes react-hook-form methods available to nested components via useFormContext
    <FormProvider {...methods}>
      <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 900, mx: 'auto', mt: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Create New Hotel
        </Typography>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Render content based on active step */}
        {/* Wrap form around content + buttons if needed, or use handleSubmit */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {' '}
          {/* Optional: wrap everything */}
          <Box mt={3} minHeight={300}>
            {renderStepContent(activeStep)}
          </Box>
          {/* Display submission error */}
          {submitError && activeStep === steps.length - 1 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
          {/* Navigation Buttons */}
          <Box mt={4} display="flex" justifyContent="space-between">
            <Button disabled={activeStep === 0 || loading} onClick={handleBack}>
              Back
            </Button>
            <Button
              // Change type to "button" if form tag is not used around content
              // type={activeStep === steps.length - 1 ? 'submit' : 'button'}
              type="button" // Use button type and handle logic in onClick
              variant="contained"
              onClick={handleNext} // handleNext now triggers validation or submit
              disabled={loading} // Only disable based on loading state now
            >
              {loading && activeStep === steps.length - 1 ? <CircularProgress size={24} color="inherit" /> : activeStep === steps.length - 1 ? 'Submit Hotel' : 'Next'}
            </Button>
          </Box>
        </form>
      </Paper>
    </FormProvider>
  );
};

export default HotelCreationWizard;
